<template>
  <div class="dashboard">
    <h1>Dashboard</h1>

    <div class="sync-section">
      <div class="sync-status">
        <h2>Sync Status</h2>
        <p v-if="syncStatus">
          Last sync: {{ formatDate(syncStatus.last_sync_at) || 'Never' }}
        </p>
        <p v-if="syncStatus && syncStatus.last_auto_update_at" class="auto-sync-info">
          Last auto-sync: {{ formatDateTime(syncStatus.last_auto_update_at) }}
        </p>
        <MonarchConnect />
      </div>
    </div>

    <div class="stats-grid">
      <router-link to="/review" class="stat-card stat-card-link stat-card-review">
        <h3>Ready to Review</h3>
        <p class="stat-value">{{ syncStatus?.counts?.ready_for_review || 0 }}</p>
        <span class="stat-link">Start review &rarr;</span>
      </router-link>

      <router-link to="/transactions?status=synced" class="stat-card stat-card-link stat-card-synced">
        <h3>Synced to Monarch</h3>
        <p class="stat-value">{{ syncStatus?.counts?.synced || 0 }}</p>
        <span class="stat-link">View transactions &rarr;</span>
      </router-link>

      <router-link to="/transactions" class="stat-card stat-card-link stat-card-unmatched">
        <h3>Unmatched Receipts</h3>
        <p class="stat-value">{{ syncStatus?.counts?.unmatched || 0 }}</p>
        <span class="stat-link">View &amp; match &rarr;</span>
      </router-link>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted } from 'vue'
import { syncAPI } from '../services/api'
import MonarchConnect from '../components/MonarchConnect.vue'

const loading = ref(false)
const syncStatus = ref(null)

onMounted(async () => {
  await loadDashboard()
})

const loadDashboard = async () => {
  try {
    loading.value = true
    const statusRes = await syncAPI.getStatus()
    syncStatus.value = statusRes.data
  } catch (error) {
    console.error('Error loading dashboard:', error)
  } finally {
    loading.value = false
  }
}

const formatDate = (dateStr) => {
  if (!dateStr) return 'N/A'
  return new Date(dateStr).toLocaleDateString()
}

const formatDateTime = (dateStr) => {
  if (!dateStr) return null
  const date = new Date(dateStr)
  return date.toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    timeZoneName: 'short'
  })
}
</script>

<style scoped>
.dashboard {
  max-width: 1200px;
  margin: 0 auto;
}

h1 {
  margin-bottom: 2rem;
  color: #2c3e50;
}

.sync-section {
  background: white;
  padding: 2rem;
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  margin-bottom: 2rem;
}

.auto-sync-info {
  color: #7f8c8d;
  font-size: 0.9rem;
  margin-top: 0.25rem;
}

.stats-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
  gap: 1.5rem;
  margin-bottom: 2rem;
}

.stat-card {
  background: white;
  padding: 1.5rem;
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.stat-card-link {
  text-decoration: none;
  cursor: pointer;
  transition: all 0.2s;
  border: 2px solid transparent;
}

.stat-card-link:hover {
  border-color: #3498db;
  box-shadow: 0 4px 12px rgba(52, 152, 219, 0.2);
}

.stat-card h3 {
  color: #7f8c8d;
  font-size: 0.9rem;
  margin-bottom: 0.5rem;
}

.stat-value {
  font-size: 2.5rem;
  font-weight: bold;
  color: #2c3e50;
  margin: 0.5rem 0;
}

.stat-link {
  color: #3498db;
  font-size: 0.9rem;
}

.stat-card-review {
  border-left: 4px solid #27ae60;
  background: linear-gradient(135deg, #fff 0%, #f0fff4 100%);
}

.stat-card-review:hover {
  border-color: #27ae60;
  box-shadow: 0 4px 12px rgba(39, 174, 96, 0.2);
}

.stat-card-review .stat-link {
  color: #27ae60;
  font-weight: 600;
}

.stat-card-synced {
  border-left: 4px solid #3498db;
  background: linear-gradient(135deg, #fff 0%, #f0f7ff 100%);
}

.stat-card-synced:hover {
  border-color: #3498db;
  box-shadow: 0 4px 12px rgba(52, 152, 219, 0.2);
}

.stat-card-unmatched {
  border-left: 4px solid #e67e22;
  background: linear-gradient(135deg, #fff 0%, #fff8f0 100%);
}

.stat-card-unmatched:hover {
  border-color: #e67e22;
  box-shadow: 0 4px 12px rgba(230, 126, 34, 0.2);
}

.stat-card-unmatched .stat-link {
  color: #e67e22;
}

.loading {
  text-align: center;
  padding: 2rem;
  color: #7f8c8d;
}
</style>
